import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Save, FileText, Shield, Loader2, Eye, Code } from 'lucide-react';
import { toast } from 'sonner';

const AdminPolicies = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('terms');
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const [termsRes, privacyRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/policies/terms`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/policies/privacy`)
      ]);

      setTermsContent(termsRes.data.content || '');
      setPrivacyContent(privacyRes.data.content || '');
    } catch (error) {
      console.error('Failed to load policies:', error);
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const content = activeTab === 'terms' ? termsContent : privacyContent;

      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/policies/${activeTab}`,
        { content }
      );

      toast.success(`${activeTab === 'terms' ? 'Terms & Conditions' : 'Privacy Policy'} updated successfully!`);
    } catch (error) {
      console.error('Failed to save policy:', error);
      toast.error('Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ padding: '80px 0' }}>
        <Loader2 className="animate-spin" style={{ width: 30, height: 30 }} color="#572B7C" />
      </div>
    );
  }

  const html = activeTab === 'terms' ? termsContent : privacyContent;
  const docTitle = activeTab === 'terms' ? 'Terms & Conditions' : 'Privacy Policy';

  const docTabs = [
    { key: 'terms', label: 'Terms & Conditions', Icon: FileText },
    { key: 'privacy', label: 'Privacy Policy', Icon: Shield },
  ];
  const modeTabs = [
    { key: false, label: 'Edit HTML', Icon: Code },
    { key: true, label: 'Preview', Icon: Eye },
  ];

  return (
    <div className="min-h-full">
      {/* Scoped reset so admin-entered HTML renders like the live page */}
      <style>{`
        .policy-preview * { all: revert; }
        .policy-preview { line-height: 1.7; color: #2B3142; font-size: 14px; }
        .policy-preview h2 { font-family: 'Poppins'; font-weight: 700; font-size: 16px; margin: 18px 0 8px; }
        .policy-preview h3 { font-family: 'Poppins'; font-weight: 700; font-size: 14px; margin: 14px 0 6px; }
        .policy-preview p { margin: 0 0 12px; }
        .policy-preview ul, .policy-preview ol { margin: 0 0 12px 1.2rem; padding: 0; }
        .policy-preview li { margin: 3px 0; }
        .policy-preview a { color: #572B7C; }
      `}</style>

      {/* Sticky header */}
      <header className="sz-header flex items-center justify-between gap-[18px] flex-wrap">
        <div>
          <h1 className="sz-h1 m-0">Policies</h1>
          <div className="text-[12.5px] text-[#8A90A2] mt-[3px]">
            Legal pages shown in the app &amp; on the website
          </div>
        </div>
        <div className="flex items-center gap-[10px]">
          <a
            href={`/${activeTab}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[12.5px] font-semibold text-[#572B7C] no-underline"
          >
            View live page ↗
          </a>
          <button
            onClick={handleSave}
            disabled={saving}
            className="sz-btn sz-btn-primary"
          >
            {saving ? (
              <Loader2 className="w-[15px] h-[15px] animate-spin" />
            ) : (
              <Save className="w-[15px] h-[15px]" />
            )}
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </header>

      {/* Tabs row */}
      <div className="px-[30px] pt-[16px] flex items-center justify-between gap-[14px] flex-wrap">
        {/* Doc tabs */}
        <div className="flex gap-[4px] bg-white border border-[#E6E9EF] p-[4px] rounded-[11px]">
          {docTabs.map((t) => {
            const on = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className="flex items-center gap-[6px] px-[18px] py-[9px] rounded-[8px] text-[13px] font-semibold cursor-pointer border-0"
                style={{ color: on ? '#fff' : '#8A90A2', background: on ? '#572B7C' : 'transparent' }}
              >
                <t.Icon className="w-[14px] h-[14px]" />
                {t.label}
              </button>
            );
          })}
        </div>
        {/* Mode tabs */}
        <div className="flex gap-[4px] bg-white border border-[#E6E9EF] p-[4px] rounded-[11px]">
          {modeTabs.map((t) => {
            const on = showPreview === t.key;
            return (
              <button
                key={String(t.key)}
                onClick={() => setShowPreview(t.key)}
                className="flex items-center gap-[6px] px-[15px] py-[9px] rounded-[8px] text-[12.5px] font-semibold cursor-pointer border-0"
                style={{ color: on ? '#fff' : '#8A90A2', background: on ? '#0C1120' : 'transparent' }}
              >
                <t.Icon className="w-[14px] h-[14px]" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Scroll body */}
      <main className="px-[30px] pt-[18px] pb-[40px]">
        <div className="max-w-[880px]">
          {!showPreview ? (
            <>
              {/* HTML-tips banner */}
              <div className="flex items-start gap-[9px] px-[14px] py-[11px] bg-[#EFF6FF] border border-[#BFDBFE] rounded-[11px] mb-[12px] text-[12px] text-[#1E40AF] leading-[1.5]">
                <Code className="w-4 h-4 flex-none mt-[1px]" color="#2563EB" />
                <span>
                  Use basic HTML: <strong>&lt;h2&gt;</strong>, <strong>&lt;p&gt;</strong>,{' '}
                  <strong>&lt;ul&gt;/&lt;li&gt;</strong>, <strong>&lt;a&gt;</strong>. Changes publish
                  to the live page on Save.
                </span>
              </div>
              <textarea
                className="szscroll sz-mono w-full resize-y px-[18px] py-[16px] border border-[#E0E3EB] rounded-[14px] text-[12.5px] leading-[1.7] text-[#2B3142] outline-none bg-white focus:border-[#28C0D0]"
                style={{ height: 440 }}
                value={html}
                onChange={(e) =>
                  activeTab === 'terms'
                    ? setTermsContent(e.target.value)
                    : setPrivacyContent(e.target.value)
                }
                placeholder="Enter HTML content here…"
              />
            </>
          ) : (
            <div className="bg-white border border-[#E6E9EF] rounded-[16px] px-[36px] py-[32px]">
              <div className="font-[Poppins] font-bold text-[24px] mb-[6px]">{docTitle}</div>
              <div className="text-[12.5px] text-[#8A90A2] mb-[22px]">
                Last updated {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
              <div
                className="policy-preview"
                dangerouslySetInnerHTML={{ __html: html }}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminPolicies;
